import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, MapPin, Globe, CheckCircle, X, Briefcase, Users, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const PERKS = [
  "Remote-first — work from anywhere",
  "Offices in Dubai, Chandigarh, Toronto",
  "Work with global brands across industries",
  "Access to every major AI tool and platform",
  "Learning budget for courses and certifications",
  "Flexible hours — results matter, not clocks",
  "Small team, big impact — your work matters here",
  "Growth path — we promote from within"
];

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [dept, setDept] = useState("All");
  const [depts, setDepts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", portfolio: "", experience: "", cover_note: "" });
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/careers`).then(r => {
      setJobs(r.data.jobs);
      const d = [...new Set(r.data.jobs.map(j => j.department))];
      setDepts(["All", ...d]);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = dept === "All" ? jobs : jobs.filter(j => j.department === dept);

  const apply = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Fill name, email, and phone."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/careers/apply`, { ...form, job_id: applying.id, job_title: applying.title });
      setSent(true); toast.success("Application submitted!");
    } catch { toast.error("Something went wrong."); }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HERO */}
      <section data-testid="careers-hero" className="pt-32 pb-24 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="careers-back-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Careers</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-4xl">
            Build the future of
            <br />
            <span className="text-zinc-500">marketing with us.</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-10 max-w-2xl leading-relaxed tracking-wider">
            We're a small team doing big things across Dubai, India, and Canada. If you're sharp, curious, and want to work at the intersection of marketing and AI — you might belong here.
          </p>
        </motion.div>
      </section>

      {/* WHY PROSPERA */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Why join us</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-xl">Not just another agency job.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-12 bg-white/10">
          {PERKS.map(p => (
            <div key={p} className="bg-black p-8 flex items-start gap-3">
              <CheckCircle className="w-3 h-3 text-zinc-500 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-zinc-400 tracking-wider leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* OPEN POSITIONS */}
      <section data-testid="careers-positions" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Open positions</p>
          <h2 className="text-2xl md:text-3xl tracking-tight">{jobs.length} roles open right now.</h2>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mt-10">
          {depts.map(d => (
            <button key={d} data-testid={`dept-filter-${d}`} onClick={() => setDept(d)}
              className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${dept === d ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
              {d}
            </button>
          ))}
        </div>

        {/* Job List */}
        <div className="mt-12 space-y-px bg-white/10">
          {filtered.map(job => (
            <div key={job.id} className="bg-black">
              <button onClick={() => setSelected(selected?.id === job.id ? null : job)}
                data-testid={`job-${job.id}`}
                className="w-full text-left p-6 md:p-8 hover:bg-white/[0.03] transition-colors flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-wider">{job.title}</p>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <span className="text-[10px] text-zinc-600 tracking-wider flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" /> {job.department}</span>
                    <span className="text-[10px] text-zinc-600 tracking-wider flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {job.location}</span>
                    <span className="text-[10px] text-zinc-600 tracking-wider flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {job.type}</span>
                    <span className="text-[10px] text-zinc-600 tracking-wider flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {job.experience}</span>
                  </div>
                </div>
                <ArrowRight className={`w-4 h-4 text-zinc-600 transition-transform ${selected?.id === job.id ? "rotate-90" : ""}`} />
              </button>

              <AnimatePresence>
                {selected?.id === job.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }} className="overflow-hidden">
                    <div className="px-6 md:px-8 pb-8 space-y-6">
                      <p className="text-zinc-400 text-xs tracking-wider leading-relaxed">{job.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-3">What you'll do</p>
                          {job.responsibilities.map((r, i) => (
                            <p key={i} className="text-[10px] text-zinc-400 tracking-wider leading-relaxed mb-2 flex items-start gap-2">
                              <span className="text-zinc-600 mt-0.5">—</span> {r}
                            </p>
                          ))}
                        </div>
                        <div>
                          <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-3">What we're looking for</p>
                          {job.requirements.map((r, i) => (
                            <p key={i} className="text-[10px] text-zinc-400 tracking-wider leading-relaxed mb-2 flex items-start gap-2">
                              <span className="text-zinc-600 mt-0.5">—</span> {r}
                            </p>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => { setApplying(job); setSent(false); setForm({ name: "", email: "", phone: "", linkedin: "", portfolio: "", experience: "", cover_note: "" }); }}
                        data-testid={`apply-${job.id}`}
                        className="bg-white text-black px-8 py-3 text-[10px] tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-2">
                        Apply now <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-zinc-600 text-xs tracking-wider mt-8">No positions in this department right now.</p>}
      </section>

      {/* APPLY MODAL */}
      <AnimatePresence>
        {applying && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setApplying(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg max-h-[90vh] overflow-y-auto p-8" data-testid="apply-modal">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="text-xs tracking-wider">Apply for {applying.title}</p>
                  <p className="text-[10px] text-zinc-600 mt-1 tracking-wider">{applying.department} · {applying.location}</p>
                </div>
                <button onClick={() => setApplying(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>

              {sent ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-8 h-8 text-white mx-auto" />
                  <p className="text-sm tracking-wider mt-4">Application received!</p>
                  <p className="text-[10px] text-zinc-500 mt-2 tracking-wider">We'll review and get back within a week.</p>
                </div>
              ) : (
                <form onSubmit={apply} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Name *</label>
                      <Input data-testid="apply-name" value={form.name} onChange={e => u("name", e.target.value)}
                        placeholder="Full name" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Email *</label>
                      <Input data-testid="apply-email" type="email" value={form.email} onChange={e => u("email", e.target.value)}
                        placeholder="you@email.com" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Phone *</label>
                      <Input data-testid="apply-phone" value={form.phone} onChange={e => u("phone", e.target.value)}
                        placeholder="+91 XXXXX XXXXX" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Experience</label>
                      <Input data-testid="apply-experience" value={form.experience} onChange={e => u("experience", e.target.value)}
                        placeholder="E.g. 3 years" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">LinkedIn profile</label>
                    <Input data-testid="apply-linkedin" value={form.linkedin} onChange={e => u("linkedin", e.target.value)}
                      placeholder="linkedin.com/in/you" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Portfolio / Resume link</label>
                    <Input data-testid="apply-portfolio" value={form.portfolio} onChange={e => u("portfolio", e.target.value)}
                      placeholder="Link to your work or resume" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-10 rounded-none text-[11px] tracking-wider" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 tracking-wider block mb-1">Why this role?</label>
                    <Textarea data-testid="apply-note" value={form.cover_note} onChange={e => u("cover_note", e.target.value)}
                      placeholder="Tell us why you're a fit..." rows={3}
                      className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-[11px] tracking-wider resize-none" />
                  </div>
                  <Button type="submit" disabled={sending} data-testid="apply-submit"
                    className="bg-white text-black hover:bg-zinc-200 rounded-none px-8 py-3 text-[10px] tracking-[0.2em] h-auto w-full disabled:opacity-50">
                    {sending ? "Sending..." : "Submit application"} {!sending && <ArrowRight className="w-3 h-3 ml-1" />}
                  </Button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
